export const ROOM_CAPACITY = 25;
export const RECONNECT_GRACE_MS = 30_000;
export const WALK_SPEED = 170;
export const PROSTRATION_MS = 4_200;
export const WORLD = { width: 1280, height: 900 };
export const WALK_BOUNDS = { left: 285, right: 995, top: 385, bottom: 795 };
export const STATUE_BOUNDS = { left: 510, right: 770, top: 55, bottom: 345 };
export type Point = { x: number; y: number };
export type Seat = Point & { id: string; kind: 'cushion' | 'bench' };
export const COURTYARD: Point[] = [{ x: 640, y: 208 }, { x: 1160, y: 465 }, { x: 640, y: 842 }, { x: 120, y: 465 }];
// The lower edges follow the courtyard diamond. The upper limit keeps guests
// clear of the raised statue and lanterns. This polygon is convex, so paths
// between any two accepted destinations remain inside it.
const lowerY = (x: number) => 842 - Math.abs(x - 640) * 377 / 520;
const bottomOffset = (842 - WALK_BOUNDS.bottom) * 520 / 377;
export const WALK_POLYGON: readonly Point[] = [
  { x: WALK_BOUNDS.left, y: WALK_BOUNDS.top }, { x: WALK_BOUNDS.right, y: WALK_BOUNDS.top },
  { x: WALK_BOUNDS.right, y: lowerY(WALK_BOUNDS.right) },
  { x: 640 + bottomOffset, y: WALK_BOUNDS.bottom }, { x: 640 - bottomOffset, y: WALK_BOUNDS.bottom },
  { x: WALK_BOUNDS.left, y: lowerY(WALK_BOUNDS.left) },
];

export const SEATS: readonly Seat[] = [...Array.from({ length: ROOM_CAPACITY }, (_, i) => ({
  id: `cushion-${i + 1}`,
  x: 420 + (i % 5) * 110,
  y: 420 + Math.floor(i / 5) * 65,
  kind: 'cushion' as const,
})), { id: 'bench-west', x: 340, y: 500, kind: 'bench' as const }, { id: 'bench-east', x: 940, y: 470, kind: 'bench' as const }];

export function isWalkable(point: Point): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y)
    && WALK_POLYGON.every((a, index) => {
      const b = WALK_POLYGON[(index + 1) % WALK_POLYGON.length];
      return (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x) >= -1e-7;
    });
}
export function clampWalkable(point: Point): Point {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return { x: 640, y: 745 };
  if (isWalkable(point)) return point;
  let closest = WALK_POLYGON[0], distance = Infinity;
  WALK_POLYGON.forEach((a, index) => {
    const b = WALK_POLYGON[(index + 1) % WALK_POLYGON.length], dx = b.x - a.x, dy = b.y - a.y;
    const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / (dx * dx + dy * dy)));
    const candidate = { x: a.x + t * dx, y: a.y + t * dy };
    const nextDistance = (point.x - candidate.x) ** 2 + (point.y - candidate.y) ** 2;
    if (nextDistance < distance) { closest = candidate; distance = nextDistance; }
  });
  return closest;
}
export function currentPosition(player: { from: Point; position: Point; movedAt: number; arrivesAt: number }, now = Date.now()): Point {
  const duration = player.arrivesAt - player.movedAt;
  const fraction = duration <= 0 ? 1 : Math.min(1, Math.max(0, (now - player.movedAt) / duration));
  return { x: player.from.x + (player.position.x - player.from.x) * fraction, y: player.from.y + (player.position.y - player.from.y) * fraction };
}
